import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Trash2, Check, PlayCircle, PauseCircle } from "lucide-react";
import { SERVICE_TAGS } from "@/lib/serviceTags";
import { PROJECT_CATEGORIES, DEFAULT_CATEGORY } from "@/lib/projectCategories";
import { projectImageUrl, updateProjectSlideshow } from "@/lib/api";

export const AdminImageCard = ({ img, onUpdated, onMove, onDelete, moving, deleting }) => {
  const [saving, setSaving] = useState(false);
  const [order, setOrder] = useState(img.sort_order ?? 0);
  const tags = img.service_tags || [];
  const included = img.in_slideshow !== false;

  const save = async (payload, okMsg) => {
    setSaving(true);
    try {
      const updated = await updateProjectSlideshow(img.id, payload);
      onUpdated(updated);
      if (okMsg) toast.success(okMsg);
    } catch (err) {
      console.error("Save slideshow failed:", err);
      toast.error("Kunde inte spara");
    } finally {
      setSaving(false);
    }
  };

  const toggleTag = (slug) => {
    const next = tags.includes(slug) ? tags.filter((t) => t !== slug) : [...tags, slug];
    save({ service_tags: next });
  };

  const commitOrder = () => {
    const n = Math.max(0, Number(order) || 0);
    setOrder(n);
    if (n !== (img.sort_order ?? 0)) save({ sort_order: n }, "Ordning sparad");
  };

  return (
    <div
      data-testid={`admin-image-${img.id}`}
      className="border-2 border-white/15 bg-white/[0.03] flex flex-col overflow-hidden"
    >
      <div className="relative aspect-[4/3] bg-[#0B132B]">
        <img
          src={projectImageUrl(img.id)}
          alt={img.filename}
          loading="lazy"
          data-testid={`admin-thumb-${img.id}`}
          className="w-full h-full object-cover"
        />
        <div
          data-testid={`admin-status-${img.id}`}
          className={`absolute top-2 left-2 inline-flex items-center gap-1 font-display font-bold text-[10px] tracking-[0.12em] uppercase px-2 py-1 ${
            included && tags.length ? "bg-[#D92525] text-white" : "bg-[#0B132B]/85 text-white/80"
          }`}
        >
          {included && tags.length ? <PlayCircle size={11} /> : <PauseCircle size={11} />}
          {!tags.length ? "Ej taggad" : included ? "I slideshow" : "Pausad"}
        </div>
        {saving && (
          <div className="absolute top-2 right-2 bg-white text-[#0B132B] p-1" data-testid={`admin-saving-${img.id}`}>
            <Loader2 size={12} className="animate-spin" />
          </div>
        )}
      </div>

      <div className="p-3 flex flex-col gap-3 flex-1">
        <div className="font-body text-[11px] text-white/50 truncate" title={img.filename}>{img.filename}</div>

        <div>
          <div className="font-display font-bold text-[10px] uppercase tracking-[0.15em] text-white/60 mb-1.5">
            Visa i tjänst
          </div>
          <div className="flex flex-wrap gap-1.5">
            {SERVICE_TAGS.map((t) => {
              const on = tags.includes(t.slug);
              return (
                <button
                  key={t.slug}
                  type="button"
                  disabled={saving}
                  onClick={() => toggleTag(t.slug)}
                  data-testid={`admin-tag-${img.id}-${t.slug}`}
                  data-checked={on}
                  aria-pressed={on}
                  className={`inline-flex items-center gap-1 border-2 px-2 py-1 font-display font-bold text-[10px] tracking-wide uppercase transition-colors disabled:opacity-60 ${
                    on ? "border-[#D92525] bg-[#D92525] text-white" : "border-white/25 text-white/75 hover:border-white"
                  }`}
                >
                  {on && <Check size={10} strokeWidth={3} />}
                  {t.title}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <label className="inline-flex items-center gap-2 cursor-pointer font-body text-xs text-white">
            <input
              type="checkbox"
              checked={included}
              disabled={saving}
              onChange={(e) => save({ in_slideshow: e.target.checked }, e.target.checked ? "Visas i slideshow" : "Pausad från slideshow")}
              data-testid={`admin-include-${img.id}`}
              className="w-4 h-4 accent-[#D92525]"
            />
            I slideshow
          </label>
          <label className="inline-flex items-center gap-1.5 font-body text-xs text-white">
            Ordning
            <input
              type="number"
              min={0}
              value={order}
              disabled={saving}
              onChange={(e) => setOrder(e.target.value)}
              onBlur={commitOrder}
              onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
              data-testid={`admin-order-${img.id}`}
              className="w-14 bg-white text-[#0B132B] font-display font-bold text-xs px-1.5 py-1 border-2 border-white"
            />
          </label>
        </div>

        <div className="mt-auto pt-3 border-t border-white/10 flex items-center gap-2">
          <select
            value={img.category || DEFAULT_CATEGORY}
            onChange={(e) => onMove(img.id, e.target.value)}
            disabled={moving}
            data-testid={`admin-move-${img.id}`}
            aria-label="Flytta till mapp"
            className="flex-1 min-w-0 bg-transparent text-white font-display font-bold text-[10px] tracking-wide uppercase px-2 py-1.5 border-2 border-white/30 cursor-pointer disabled:opacity-60"
          >
            {PROJECT_CATEGORIES.map((cat) => (
              <option key={cat.slug} value={cat.slug} className="text-[#0B132B]">
                {cat.title}
              </option>
            ))}
          </select>
          <button
            onClick={() => onDelete(img.id)}
            disabled={deleting}
            data-testid={`admin-delete-${img.id}`}
            aria-label="Radera bild"
            className="shrink-0 border-2 border-[#D92525] text-[#FFB3B3] hover:bg-[#D92525] hover:text-white p-1.5 transition-colors disabled:opacity-60"
          >
            {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
          </button>
        </div>
      </div>
    </div>
  );
};
